// A builder page's drafts, kept outside its editors (doc/VerbaScholaBuilderUnsavedWork.md). While an editor's typed
// values differ from the saved lesson, they live here, keyed by what they edit (`vocabulary_target:7`,
// `plan_section:new:stage`, `specialized:91`). An editor removed from the page (another activity shown, a view changed)
// keeps its draft, shown again when it opens. On Lesson plan the store also holds each editor's save, and Save all saves
// drafts whose editors are closed; Exercises keeps its edit scopes and their saves itself (exerciseSaveAll.ts) and tells
// the store which keys are held. A draft is cleared only by its own save, its editor's Cancel, or a refresh of the page's
// content (which asks first). Proposal review edits are not drafts here: an editor in a review keeps its values to
// itself, as the review stores them.
import React from 'react';
import { LessonReviewReadOnlyContext } from './lessonReviewReadOnly';

export interface DraftSave {
    /** Said in Save all's messages and the leave dialog. */
    label: string;
    /** Whether the draft can be saved as it is (its required fields). */
    valid: boolean;
    /** Saves the draft; rejects when it was not saved. */
    save: () => Promise<unknown>;
}

export interface PendingDraft {
    key: string;
    label: string;
    valid: boolean;
}

interface DraftRecord {
    fields: Map<string, unknown>;
    pending: boolean;
    version: number;
    actions: DraftSave | null;
    mounted: number;
}

export class DraftStore {
    private records = new Map<string, DraftRecord>();
    private listeners = new Set<() => void>();
    private shown: PendingDraft[] = [];

    /** ``held``: for a page that tracks its unsaved edits itself (Exercises, by edit scope), whether a key's draft is
     * unsaved. Without it, each editor says so (useDraftSave). */
    constructor(private readonly held?: (key: string) => boolean) {}

    private holds(key: string, record: DraftRecord) {
        return this.held ? this.held(key) : record.pending;
    }

    private record(key: string) {
        let record = this.records.get(key);
        if (!record) {
            record = { fields: new Map(), pending: false, version: 0, actions: null, mounted: 0 };
            this.records.set(key, record);
        }
        return record;
    }

    private changed() {
        this.shown = [...this.records.entries()]
            .filter(([, record]) => record.pending && record.actions)
            .map(([key, record]) => ({ key, label: record.actions!.label, valid: record.actions!.valid }));
        this.listeners.forEach(listener => listener());
    }

    subscribe = (listener: () => void) => {
        this.listeners.add(listener);
        return () => { this.listeners.delete(listener); };
    };

    /** The pending drafts, in the order their editors first drew (stable between changes, for useSyncExternalStore). */
    pending = () => this.shown;

    /** A field's value kept for a pending draft (an editor opening again shows it). An editor of a key with no draft
     * starts from the saved values: what an earlier editor of it left (values since saved or cancelled) goes. */
    field(key: string, name: string): { kept: boolean; value?: unknown } {
        const record = this.records.get(key);
        if (!record) return { kept: false };
        if (!this.holds(key, record)) { record.fields.clear(); return { kept: false }; }
        return record.fields.has(name) ? { kept: true, value: record.fields.get(name) } : { kept: false };
    }

    /** Whether a draft of ``key`` is unsaved. */
    holdsDraft(key: string) {
        const record = this.records.get(key);
        return Boolean(record && this.holds(key, record));
    }

    setField(key: string, name: string, value: unknown) {
        const record = this.record(key);
        record.fields.set(name, value);
        record.version++;
    }

    /** An editor says, each time it draws, whether it holds a draft, and how to save it. Its fields are kept while it
     * does not (an editor opened but not changed yet): only a pending draft's are shown again. */
    setPending(key: string, pending: boolean, actions: DraftSave) {
        const record = this.record(key);
        const changed = record.pending !== pending || record.actions?.label !== actions.label || record.actions?.valid !== actions.valid;
        record.pending = pending;
        record.actions = actions;
        if (changed) this.changed();
    }

    mount(key: string) {
        this.record(key).mounted++;
    }

    unmount(key: string) {
        const record = this.records.get(key);
        if (!record) return;
        record.mounted--;
        // A closed editor's draft stays; one with no changes goes.
        if (record.mounted <= 0 && !this.holds(key, record)) this.records.delete(key);
    }

    /** Saves a pending draft with its editor's current save. The draft is cleared afterwards unless it changed while
     * the save ran; an open editor clears its own, following the saved values. */
    async save(key: string) {
        const record = this.records.get(key);
        if (!record?.pending || !record.actions) return;
        const version = record.version;
        await record.actions.save();
        const after = this.records.get(key);
        if (!after || after.version !== version || after.mounted > 0) return;
        this.records.delete(key);
        this.changed();
    }

    /** Drops a draft (its editor's Cancel). An editor still open keeps its place, with nothing kept. */
    clear(key: string) {
        const record = this.records.get(key);
        if (!record) return;
        const shown = record.pending;
        this.drop(key, record);
        if (shown) this.changed();
    }

    /** Drops every draft: the page's content was loaded again (a Refresh, which asks first). */
    clearAll() {
        const shown = this.shown.length > 0;
        for (const [key, record] of [...this.records.entries()]) this.drop(key, record);
        if (shown) this.changed();
    }

    private drop(key: string, record: DraftRecord) {
        if (record.mounted <= 0) { this.records.delete(key); return; }
        record.fields.clear();
        record.pending = false;
        record.version++;
    }
}

export const BuilderDraftsContext = React.createContext<DraftStore | null>(null);

/** The page's draft store; another lesson starts a new one. ``held``, read when an editor opens: see DraftStore. */
export function useDraftStore(scope: string | number, held?: (key: string) => boolean) {
    return React.useMemo(() => new DraftStore(held), [scope]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** The lesson revision the page last confirmed (its own writes, reads it applied), read when a save is sent. A draft
 * whose editor has closed is saved after that editor's last render: the revision it drew with may be older. */
export const ConfirmedRevisionContext = React.createContext<(() => number) | null>(null);

/** A function giving the page's confirmed revision when called (``revision``, the editor's prop, without a page). */
export function useConfirmedRevision(revision: number) {
    const confirmed = React.useContext(ConfirmedRevisionContext);
    const latest = React.useRef(revision);
    latest.current = revision;
    return React.useCallback(() => confirmed ? confirmed() : latest.current, [confirmed]);
}

/** The store's pending drafts, re-rendering when they change. */
export function usePendingDrafts(store: DraftStore | null) {
    return React.useSyncExternalStore(store?.subscribe ?? noSubscribe, store?.pending ?? noDrafts);
}
const NONE: PendingDraft[] = [];
const noSubscribe = () => () => undefined;
const noDrafts = () => NONE;

/** An editor's field, kept with its draft (``key``) while that is pending; like React.useState otherwise. Without a
 * store or a key, or in a proposal review, it is plain state. */
export function useDraftState<T>(key: string | null, name: string, initial: T | (() => T)): [T, React.Dispatch<React.SetStateAction<T>>] {
    const store = React.useContext(BuilderDraftsContext);
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const kept = store && key && !readOnly ? store : null;
    // A setter called after the editor's key changed (a deleted item's card, now empty, or a new item saved) writes under
    // the key the editor has now.
    const target = React.useRef({ kept, key });
    target.current = { kept, key };
    const [value, setValue] = React.useState<T>(() => {
        if (kept && key) {
            const stored = kept.field(key, name);
            if (stored.kept) return stored.value as T;
        }
        return typeof initial === 'function' ? (initial as () => T)() : initial;
    });
    const set = React.useCallback<React.Dispatch<React.SetStateAction<T>>>(action => setValue(current => {
        const next = typeof action === 'function' ? (action as (previous: T) => T)(current) : action;
        const { kept: draftStore, key: draftKey } = target.current;
        if (draftStore && draftKey) draftStore.setField(draftKey, name, next);
        return next;
    }), [name]);
    return [value, set];
}

/** Whether the store holds a draft of ``key`` as an editor opens: a part folded by default (a details element) then
 * opens, so the kept draft is seen. */
export function useDraftKept(key: string | null) {
    const store = React.useContext(BuilderDraftsContext);
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const [kept] = React.useState(() => Boolean(store && key && !readOnly && store.holdsDraft(key)));
    return kept;
}

/** An editor's draft: whether it holds one (``pending``) and how to save it. The registration outlives the editor while
 * pending, so Save all saves it after the editor has closed. Returns ``discard``, for its Cancel. */
export function useDraftSave(key: string, pending: boolean, draft: DraftSave) {
    const store = React.useContext(BuilderDraftsContext);
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const latest = React.useRef(draft);
    latest.current = draft;
    const save = React.useCallback(() => latest.current.save(), []);
    const active = Boolean(store) && !readOnly;
    React.useLayoutEffect(() => {
        if (active) store!.setPending(key, pending, { label: draft.label, valid: draft.valid, save });
    });
    React.useLayoutEffect(() => {
        if (!active) return;
        store!.mount(key);
        return () => store!.unmount(key);
    }, [active, store, key]);
    // An editor whose key changes (a new item, once saved, is named by its id) leaves nothing under its old key: its
    // draft is the editor's, kept under the new one. This holds for its own Save and for Save all alike.
    const drawnKey = React.useRef(key);
    React.useLayoutEffect(() => {
        if (drawnKey.current === key) return;
        if (active) store!.clear(drawnKey.current);
        drawnKey.current = key;
    }, [active, store, key]);
    return React.useCallback(() => { if (active) store!.clear(key); }, [active, store, key]);
}
